import * as React from "react";
import AppBar from "../../components/atoms/appbar";
import Button from "../../components/atoms/button";
import MoreDrawer from "../../components/atoms/drawer/MoreDrawer";
import { useParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  useCommnuityArticleDetail,
  useCommunityArticleComment,
} from "../../@apis/hooks/useCommunity";
import { formatKoreanDate } from "../../lib/utils/dateUtils";
import { staticURL } from "../../lib/utils/imageUtils";
import type { CommunityArticleLikesRequest } from "../../@apis/dto/request/community.request";
import { flushSync } from "react-dom";
import {
  communityArticleCommentWrite,
  communityArticleLikes,
} from "../../@apis/v1/community.service";
import { getQueryClient } from "../../@apis/queryClient";
import { cn } from "../../lib/utils/cn";
import { useLoginInfo } from "../../hooks/useLoginInfo";
import { useModalStore } from "../../lib/store/useModalStore";
import { LOGIN_DIALOG_LABEL } from "../../lib/constants/common.constants";
import { useRouter } from "../../hooks/useRouter";
import { useLocationStore } from "../../lib/store/useLocationStore";
import type { OnErrorResponse } from "../../@apis/dto/response/common.response";

function CommentMeta({
  nickname,
  updateDt,
}: {
  nickname?: string;
  updateDt?: string;
}) {
  const metaRef = React.useRef<HTMLDivElement>(null);
  const nicknameRef = React.useRef<HTMLParagraphElement>(null);
  const dateRef = React.useRef<HTMLParagraphElement>(null);
  const [isWrapped, setIsWrapped] = React.useState(false);

  React.useEffect(() => {
    const container = metaRef.current;
    const nicknameEl = nicknameRef.current;
    const dateEl = dateRef.current;
    if (!container || !nicknameEl || !dateEl) return;

    const checkWrap = () => {
      setIsWrapped(
        nicknameEl.getBoundingClientRect().top !==
          dateEl.getBoundingClientRect().top,
      );
    };

    checkWrap();
    const observer = new ResizeObserver(checkWrap);
    observer.observe(container);
    return () => observer.disconnect();
  }, [nickname, updateDt]);

  return (
    <div
      ref={metaRef}
      className="flex items-center flex-wrap gap-[10px] pb-[15px] pr-[26px]"
    >
      <p ref={nicknameRef} className="font-[500] text-sm whitespace-nowrap">
        {nickname}
      </p>
      <div
        className={cn(
          "w-[1px] border-l border-[#e6e6e6] h-3",
          isWrapped && "invisible",
        )}
      />
      <p
        ref={dateRef}
        className="font-[500] text-[#888] text-sm whitespace-nowrap"
      >
        {formatKoreanDate(updateDt || "")}
      </p>
    </div>
  );
}

export default function CommunityDetail() {
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const { push, back } = useRouter();
  const { id } = useParams();
  const { isLoggedIn } = useLoginInfo();

  // STORE
  const { openModal } = useModalStore();
  const { locate } = useLocationStore();

  const [comment, setComment] = React.useState("");
  const [modalItem, setModalItem] = React.useState({
    isMyItem: false,
    type: "ARTICLE",
    articleId: id,
    commentId: 0,
  });
  const triggerRef = React.useRef(null);
  const metaRef = React.useRef<HTMLDivElement>(null);
  const nicknameRef = React.useRef<HTMLParagraphElement>(null);
  const dateRef = React.useRef<HTMLParagraphElement>(null);
  const [isMetaWrapped, setIsMetaWrapped] = React.useState(false);

  const { data: commnuityData } = useQuery({
    ...useCommnuityArticleDetail(id as string),
    enabled: !!id,
  });
  const { article } = commnuityData?.data || {};

  const {
    data: commentData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...useCommunityArticleComment(id as string),
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
  });
  const commentPages = commentData?.pages || [];

  const commentMutation = useMutation({
    mutationFn: ({ id, content }: { id: string | number; content: string }) =>
      communityArticleCommentWrite(id, content),
    onSuccess: (res) => {
      const { success, error } = res;

      if (success) {
        getQueryClient().invalidateQueries({
          queryKey: ["community-article-comment", id],
        });
        getQueryClient().invalidateQueries({
          queryKey: ["community-article-detail", id],
        });
        // 목록 카드의 댓글 수도 갱신되도록 목록 진입 시 제자리 갱신 플래그를 남긴다.
        sessionStorage.setItem("community_needs_refresh", "true");
      } else {
        openModal("ALERT_DIALOG", {
          title: "에러",
          content:
            error?.code === "C003" ? "요청에 실패하였습니다." : error?.message,
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "에러",
        content:
          error?.code === "C003" ? "요청에 실패하였습니다." : error?.message,
      });
    },
  });

  const likeMutation = useMutation({
    mutationFn: (form: CommunityArticleLikesRequest) =>
      communityArticleLikes(form),
    onSuccess: (res) => {
      const { success, error } = res;

      if (success) {
        getQueryClient().invalidateQueries({
          queryKey: ["community-article-detail", id],
        });
        // 목록 카드의 좋아요 수도 갱신되도록 목록 진입 시 제자리 갱신 플래그를 남긴다.
        sessionStorage.setItem("community_needs_refresh", "true");
      } else {
        alert(error?.message);
      }
    },
  });

  React.useEffect(() => {
    const container = metaRef.current;
    const nickname = nicknameRef.current;
    const date = dateRef.current;
    if (!container || !nickname || !date) return;

    const checkWrap = () => {
      setIsMetaWrapped(
        nickname.getBoundingClientRect().top !==
          date.getBoundingClientRect().top,
      );
    };

    checkWrap();
    const observer = new ResizeObserver(checkWrap);
    observer.observe(container);
    return () => observer.disconnect();
  }, [article?.nickname, article?.updateDt]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: document.querySelector("main"), threshold: 0 },
    );

    const currentTrigger = triggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="bg-[#f5f5f5] min-h-full flex flex-col">
      <AppBar
        onBack={() => {
          const prevLocate = locate.previousLocation?.pathname;
          if (prevLocate?.startsWith("/community/edit/")) {
            back(-2);
          } else {
            back();
          }
        }}
        right={
          <Button
            className="p-0"
            label=""
            onClick={() => {
              if (!isLoggedIn)
                return openModal("CONFIRM_DIALOG", {
                  title: LOGIN_DIALOG_LABEL.title,
                  content: LOGIN_DIALOG_LABEL.content,
                  onClick: () => push("/login"),
                });
              flushSync(() => {
                setModalItem((prev) => ({
                  ...prev,
                  isMyItem: article?.isMyArticle || false,
                  type: "ARTICLE",
                }));
              });
              setOpenDrawer(true);
            }}
          >
            <img className="w-[20px] h-[20px]" src="/icons/ic-set.webp" />
          </Button>
        }
      />
      <div className="relative flex-1">
        <section>
          <div className="w-full bg-white px-4 pb-[25px]">
            <div className="flex-row gap-[5px]">
              <img className="w-[0.75rem]" src="/icons/ic-flag.webp" />
              <p className="font-[500] text-sm">{article?.golfParkName}</p>
            </div>
            <div className="py-[20px] border-b border-[#e6e6e6] ">
              <div ref={metaRef} className="flex-row gap-[10px] flex-wrap">
                <p
                  ref={nicknameRef}
                  className="font-[500] text-sm whitespace-nowrap"
                >
                  {article?.nickname}
                </p>
                <div
                  className={cn(
                    "w-[1px] h-2.5 bg-[#e6e6e6]",
                    isMetaWrapped && "invisible",
                  )}
                />

                <p
                  ref={dateRef}
                  className="font-[500] text-[#888] text-sm whitespace-nowrap"
                >
                  {formatKoreanDate(article?.updateDt || "")}
                </p>
              </div>
              <p className="font-bold mt-[15px] text-xl">{article?.title}</p>
            </div>

            <div className="space-y-[14px]">
              <div className="mt-[14px]">
                <span className="font-[500] text-[#222] break-all whitespace-pre-wrap">
                  {article?.content}
                </span>
              </div>

              {(article?.fileList || []).map((item, index) => (
                <img
                  className="rounded-[15px] w-full h-auto"
                  src={staticURL(item.fileUrl)}
                  key={index}
                />
              ))}

              <div className="flex-row-between mt-[30px]">
                <div
                  className={cn(
                    "flex-row gap-[10px] border rounded-full px-[14px] py-[10px] cursor-pointer",
                    article?.likeItemId
                      ? "bg-[#e5002b] border-[#e5002b]"
                      : " border-[#e6e6e6]",
                  )}
                  onClick={() => {
                    if (!isLoggedIn)
                      return openModal("CONFIRM_DIALOG", {
                        title: LOGIN_DIALOG_LABEL.title,
                        content: LOGIN_DIALOG_LABEL.content,
                        onClick: () => push("/login"),
                      });
                    likeMutation.mutate({
                      likeItemId: id as string,
                      likeItemType: "ARTICLE",
                    });
                  }}
                >
                  <img src="/icons/ic-heart-pink.webp" className="size-5" />
                  <span
                    className={cn(
                      "font-[500]",
                      !article?.likeItemId ? "text-[#222]" : "text-white",
                    )}
                  >
                    좋아요
                  </span>
                  <span
                    className={cn(
                      "font-[500] text-sm",
                      !article?.likeItemId ? "text-[#888]" : "text-white",
                    )}
                  >
                    {article?.likeCount}개
                  </span>
                </div>
                <div className="flex-row gap-[5px]">
                  <img
                    className="size-[0.88rem]"
                    src="/icons/ic-comment.webp"
                  />
                  <span className="font-[500] text-[#888] text-sm">
                    {article?.commentCount}개
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="px-4 py-6 space-y-[15px]">
          {(commentPages || []).map((pageData, pageIndex) => (
            <React.Fragment key={pageIndex}>
              {(pageData.data.commentList || []).map((item, index, arr) => {
                const isLast =
                  pageIndex === commentPages.length - 1 &&
                  index === arr.length - 1;
                return (
                  <div
                    key={index}
                    className="relative bg-white rounded-[15px] p-[20px]"
                    ref={isLast ? triggerRef : undefined}
                  >
                    {item.commentStatus !== "DELETED_BY_REGISTRANT" && (
                      <Button
                        className="absolute top-[15px] right-[15px] p-[8px] border border-[#e6e6e6] rounded-[8px] bg-white shadow-rose-50 "
                        label=""
                        disabled={
                          item.commentStatus === "DELETED_BY_REGISTRANT"
                        }
                        onClick={() => {
                          if (!isLoggedIn)
                            return openModal("CONFIRM_DIALOG", {
                              title: LOGIN_DIALOG_LABEL.title,
                              content: LOGIN_DIALOG_LABEL.content,
                              onClick: () => push("/login"),
                            });
                          flushSync(() => {
                            setModalItem((prev) => ({
                              ...prev,
                              isMyItem: item.isMyComment || false,
                              type: "COMMENT",
                              commentId: item.id,
                            }));
                          });
                          setOpenDrawer(true);
                        }}
                      >
                        <img
                          className="size-[0.88rem]"
                          src="/icons/ic-set.webp"
                        />
                      </Button>
                    )}
                    <CommentMeta
                      nickname={item.nickname}
                      updateDt={item.updateDt}
                    />
                    <p
                      className={cn(
                        "font-[500] break-all pr-7 text-sm",
                        item.commentStatus === "DELETED_BY_REGISTRANT"
                          ? "text-[#888]"
                          : "text-[#222]",
                      )}
                    >
                      {item.content}
                    </p>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </section>
      </div>
      <div className="p-[15px] w-full sticky bottom-0 bg-white">
        <div className="flex-row border border-[#e6e6e6] rounded-[10px] w-full text-[14px] pr-[18px]">
          <input
            className="w-full h-full p-[18px] focus:border-none focus:outline-none focus:ring-0  text-[0.7rem] placeholder:text-[0.7rem]"
            placeholder="댓글을 입력해주세요."
            readOnly={!isLoggedIn}
            value={comment}
            maxLength={200}
            onChange={(e) => setComment(e.target.value)}
            onClick={() => {
              if (!isLoggedIn)
                return openModal("CONFIRM_DIALOG", {
                  title: LOGIN_DIALOG_LABEL.title,
                  content: LOGIN_DIALOG_LABEL.content,
                  onClick: () => push("/login"),
                });
            }}
          />
          <img
            className="size-5"
            src={
              comment.length > 0
                ? "/icons/ic-send.webp"
                : "/icons/ic-send-de.webp"
            }
            onClick={() => {
              if (comment.length > 0) {
                commentMutation.mutate({ id: id as string, content: comment });
                setComment("");
              }
            }}
          />
        </div>
      </div>
      {openDrawer && (
        <MoreDrawer
          open={openDrawer}
          isMyItem={modalItem.isMyItem}
          storeCode={article?.golfParkCode || ""}
          onClick={(value: string) => {
            if (value == "COMMENT_EDIT") {
              const _comment = commentPages
                .map((page) => page.data.commentList)
                .flat()
                .find((item) => item.id == modalItem.commentId);
              setComment(_comment?.content || "");
            }
          }}
          type={modalItem.type as "ARTICLE" | "COMMENT"}
          articleId={modalItem.articleId as string}
          commentId={modalItem.commentId}
          onClose={() => setOpenDrawer(false)}
        />
      )}
    </div>
  );
}
